import type { MutableRefObject } from "react";
import { useState } from "react";

import {
  IconButton,
  PopoverWithHighlightedAnchor,
  Tooltip,
} from "@braintrust/braintrust-ui-components";
import { useMediaQuery } from "@braintrust/braintrust-ui-components";
import { CalendarIcon } from "@braintrust/braintrust-ui-components/Icons";
import { openSchedulePromptModal } from "@js/apps/messenger/components/schedule-prompt-modal";
import type { User } from "@js/types/auth";
import type { MessageRoom } from "@js/types/messenger";

import { useSendCalendarLinkPromptShown } from "../../hooks/use-send-calendar-link-prompt-shown";

import { SchedulePromptPopoverContent } from "./schedule-prompt-popover-content";

type SchedulePromptProps = {
  user: User;
  activeRoom: MessageRoom;
  className: string;
};

export const SchedulePrompt = ({
  user,
  activeRoom,
  className,
}: SchedulePromptProps) => {
  const recipientId = activeRoom.interlocutors[0];
  const recipient = activeRoom.participants.find(
    (roomUser) => roomUser.id === recipientId,
  );
  const sender = activeRoom.participants.find(
    (roomUser) => roomUser.id !== recipientId,
  );
  const isImpersonatedSession = user?.is_impersonated_session;

  const { shouldShowCalendarLinkPrompt, setShouldShowCalendarPromptFalse } =
    useSendCalendarLinkPromptShown(activeRoom, isImpersonatedSession);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isMobile = useMediaQuery("sm");

  const handleScheduleCallClick = (onClick: () => void) => {
    if (isPopoverOpen) return setIsPopoverOpen(false);

    if (shouldShowCalendarLinkPrompt) {
      setShouldShowCalendarPromptFalse();
      onClick();
    } else {
      openSchedulePromptModal();
    }
  };

  if (!recipient || !sender) return null;

  return (
    <PopoverWithHighlightedAnchor
      disablePortal
      onOpenChange={(isOpen) => setIsPopoverOpen(isOpen)}
      open={isPopoverOpen}
      renderAnchor={({ isOpen, anchorProps }) => {
        return (
          <Tooltip title="Add a calendar link" placement="top">
            <IconButton
              variant="transparent"
              aria-label="Open calendar"
              size="medium"
              sx={{
                "&": isOpen ? { backgroundColor: "white !important" } : {},
                "&:hover": { backgroundColor: "var(--yellow) !important" },
              }}
              {...anchorProps}
              ref={anchorProps.ref as MutableRefObject<HTMLButtonElement>}
              className={className}
              onClick={(ev: React.MouseEvent<HTMLElement>) => {
                handleScheduleCallClick(() => anchorProps.onClick(ev));
              }}
            >
              <CalendarIcon sx={{ path: { strokeWidth: "1" } }} />
            </IconButton>
          </Tooltip>
        );
      }}
      transformOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      offsetY={-16}
      transitionDuration={0}
      slotProps={{
        paper: {
          sx: !isMobile
            ? {
                padding: "32px",
                paddingBottom: "24px",
                border: "1px solid var(--grey-4)",
              }
            : {
                padding: "24px",
                border: "1px solid var(--grey-4)",
                maxHeight: "calc(100% - 80px)",
                maxWidth: "calc(100% - 80px)",
              },
        },
      }}
    >
      <SchedulePromptPopoverContent
        sender={sender}
        recipient={recipient}
        setIsOpen={setIsPopoverOpen}
      />
    </PopoverWithHighlightedAnchor>
  );
};
